import { useEffect, useState } from 'react';
import { api, orderApi } from '../api';
import { takeOrderPayment } from '../beamPayment';
import { ErrorNote, Modal, Spinner, dateOf, money, th, timeOf, useLoader } from './common';
import { IconGuest, IconPin, IconClock } from './icons';
import PaymentMethodPicker from './PaymentMethodPicker';

const dt = (iso) => (iso ? `${dateOf(iso)} (${timeOf(iso)})` : '-');

const range = (a, b) =>
  a ? `${dateOf(a)} (${timeOf(a)}${b ? ` - ${timeOf(b)}` : ''})` : '-';

const Field = ({ label, children }) => (
  <div className="dd-field">
    <span className="dd-label">{label}</span>
    <div className="dd-value">{children}</div>
  </div>
);

/**
 * รายละเอียด — the right-hand slide-over opened from a card on the board.
 *
 * Shows the whole party, not just the card that was clicked: order code, the
 * booked window against the window actually served, the audit stamps, every
 * guest with their service and therapist, then the payment block once a bill
 * exists.
 */
export default function DetailDrawer({
  appointmentId, autoPay = false, onClose, onChanged,
}) {
  const [paying, setPaying] = useState(Boolean(autoPay));
  const detail = useLoader(
    () => api.get(`/api/system/appointments/${appointmentId}/detail`),
    [appointmentId],
  );

  useEffect(() => {
    setPaying(Boolean(autoPay));
  }, [appointmentId, autoPay]);

  const d = detail.data?.detail;
  const guests = detail.data?.guests || [];
  const payments = detail.data?.payments || [];

  return (
    <div className="dd-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="dd-panel">
        <div className="dd-head">
          <h2>รายละเอียด</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {detail.loading ? <Spinner /> : !d ? (
          <div className="dd-body">ไม่พบรายการ</div>
        ) : (
          <>
            <div className="dd-body">
              <div className="dd-code">▤ #{d.order_code || d.booking_code}</div>

              <div className="dd-grid">
                <Field label="ลูกค้า">{d.customer_name || 'ลูกค้าไม่ระบุชื่อ'}</Field>
                <Field label="เบอร์โทรศัพท์">{d.customer_phone || '-'}</Field>

                <Field label="ประเภทการจอง">{th(d.channel)}</Field>
                <Field label="ช่องทางการจอง">{d.agency_name || '-'}</Field>

                <Field label="เวลาจอง">{range(d.first_start, d.last_end)}</Field>
                <Field label="เวลาเริ่มบริการ">{range(d.first_started, d.last_finished)}</Field>

                <Field label="ประเภทการทำรายการ">
                  <span className="dd-chip"><IconGuest size={14} /> {th(d.order_type || 'service')}</span>
                </Field>
                <Field label="สถานะ">
                  <span className={`badge ${d.order_status === 'completed' ? 'badge-green' : 'badge-grey'}`}>
                    {d.order_status === 'completed' ? 'เสร็จสิ้น' : th(d.order_status || 'open')}
                  </span>
                </Field>

                <Field label="สร้างโดย">
                  {d.created_by_name ? `${d.created_by_name}, ${dt(d.created_at)}` : '-'}
                </Field>
                <Field label="แก้ไขโดย">
                  {d.updated_by_name ? `${d.updated_by_name}, ${dt(d.updated_at)}` : '-'}
                </Field>
              </div>

              <Field label="หมายเหตุ">{d.note || d.order_note || '-'}</Field>

              <div className="dd-section">
                <IconGuest size={16} /> ลูกค้า <span className="dd-count">{guests.length}</span>
              </div>

              {guests.map((g, i) => (
                <div className="dd-guest" key={g.appointment_id}>
                  <div className="dd-guest-head">
                    <strong>{g.guest_no ?? i + 1}. {g.name || 'ลูกค้าไม่ระบุชื่อ'}</strong>
                    <span className="dd-price">{money(g.unit_price)}</span>
                  </div>
                  <div className="dd-guest-line">
                    {g.name_en} {g.name_th}
                    <span className="dot">•</span>
                    <IconClock size={12} /> {g.duration_minutes} นาที
                    <span className="dot">•</span>
                    {g.therapists || 'ยังไม่ระบุหมอ'}
                    {g.bed_code && (<><span className="dot">•</span><IconPin size={12} /> {g.bed_code}</>)}
                  </div>
                  <div className="dd-guest-note">{g.note || '-'}</div>
                </div>
              ))}

              {d.order_id && (
                <>
                  <div className="dd-section">฿ ชำระเงิน</div>
                  <div className="dd-grid">
                    <Field label="ราคารวม">{money(d.grand_total)}</Field>
                    <Field label="ยอดชำระสุทธิ">{money(d.paid_total)}</Field>
                    <Field label="ช่องทางการชำระเงิน">
                      {payments.map((p) => th(p.method)).join(', ') || '-'}
                    </Field>
                    <Field label="จำนวนเงิน">
                      {payments.length ? money(payments.reduce((s, p) => s + Number(p.amount), 0)) : '-'}
                    </Field>
                    <Field label="เวลาชำระเงิน">{dt(payments[0]?.paid_at || d.closed_at)}</Field>
                    <Field label="สถานะการชำระเงิน">
                      <span className={`badge ${d.payment_status === 'paid' ? 'badge-green' : 'badge-red'}`}>
                        {th(d.payment_status)}
                      </span>
                    </Field>
                  </div>
                  <Field label="หมายเหตุการชำระเงิน">{payments[0]?.note || '-'}</Field>
                </>
              )}
            </div>

            <div className="dd-actions">
              {d.payment_status !== 'paid' && (
                <button className="btn-solid dd-pay" onClick={() => setPaying(true)}>
                  ชำระเงิน
                </button>
              )}
              <button className="btn-outline dd-print" onClick={() => window.print()}>พิมพ์</button>
            </div>

            {paying && (
              <PaymentDialog
                detail={d}
                guests={guests}
                onClose={() => setPaying(false)}
                onPaid={() => { setPaying(false); detail.reload(); onChanged?.(); }}
              />
            )}
          </>
        )}
      </aside>
    </div>
  );
}

/**
 * ชำระเงิน — takes the money for a booking.
 *
 * A walk-in booked on the board has no bill until somebody pays, so the first
 * payment opens the order too. Both steps are done here rather than leaving the
 * ชำระเงิน button pointing at a screen that could only show the total.
 */
function PaymentDialog({ detail: d, guests, onClose, onPaid }) {
  const due = Number(d.grand_total || 0) - Number(d.paid_total || 0);
  // No order yet means no grand_total, so the amount owed is the party's lines.
  const fallback = guests.reduce(
    (sum, g) => sum + Number(g.unit_price || 0) - Number(g.discount_amount || 0),
    0,
  );
  const [form, setForm] = useState({
    method: 'cash',
    amount: String(due > 0 ? due : fallback),
  });
  const [cashReceived, setCashReceived] = useState(
    String(due > 0 ? due : fallback)
  );
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const amount = Number(form.amount);
    if (!(amount > 0)) return setError(new Error('จำนวนเงินต้องมากกว่า 0'));
    if (form.method === 'cash' && Number(cashReceived) < amount) {
      return setError(new Error('จำนวนเงินสดที่ลูกค้าให้ยังไม่พอชำระ'));
    }

    setBusy(true);
    setError(null);
    try {
      let orderId = d.order_id;
      if (!orderId) {
        const created = await orderApi.create({
          bookingId: d.booking_id,
          orderType: 'service',
          appointmentIds: guests.map((g) => g.appointment_id ?? g.id).filter(Boolean),
        });
        orderId = created.order?.id ?? created.id;
      }
      await takeOrderPayment({
        orderId,
        method: form.method,
        amount,
        onWaiting: () => setWaiting(true),
      });
      onPaid();
    } catch (e) {
      setError(e);
      setBusy(false);
      setWaiting(false);
    }
  };

  return (
    <Modal title="ชำระเงิน" onClose={onClose}>
      <ErrorNote error={error} />

      <div className="summary-row">
        <span>ยอดรวม</span>
        <strong>{money(d.grand_total || fallback)}</strong>
      </div>
      {Number(d.paid_total) > 0 && (
        <div className="summary-row">
          <span>ชำระแล้ว</span>
          <strong>{money(d.paid_total)}</strong>
        </div>
      )}

      <PaymentMethodPicker
        method={form.method}
        onMethod={(method) => setForm((current) => ({ ...current, method }))}
        amount={form.amount}
        cashReceived={cashReceived}
        onCashReceived={setCashReceived}
        waiting={waiting}
      />

      <label className="field">
        <span>จำนวนเงิน</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.amount}
          onChange={set('amount')}
          disabled={waiting}
        />
      </label>

      <div className="modal-actions payment-modal-actions">
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={onClose} disabled={waiting}>ยกเลิก</button>
        <button className="btn btn-solid" onClick={submit} disabled={busy}>
          {waiting ? 'กำลังรอ Beam…' : busy ? 'กำลังบันทึก…' : 'รับเงิน'}
        </button>
      </div>
    </Modal>
  );
}
